import { DataDrivenPropertyValueSpecification, LayerSpecification } from "mapbox-gl";

import { makeOnHoverExpression } from "./makeOnHoverExpression";

export default function generateDefaultMapStyle(
    sourceId: string,
    color = "#FFFFFF",
    isLayerSelected: boolean,
    selectedFeatureId?: string,
    hoveredFeatureId?: string,
): LayerSpecification[] {
    const defaultRadius = isLayerSelected ? 6 : 4;
    const defaultOpacity = isLayerSelected ? 1 : 0.6;
    const highlightColor = "#FFA500";

    const makeConditionalFeatureExpression = (baseColor: string): DataDrivenPropertyValueSpecification<string> | undefined => {
        const conditions: any[] = [];

        if (selectedFeatureId) {
            conditions.push(["==", ["get", "id"], selectedFeatureId], highlightColor);
        }

        if (hoveredFeatureId) {
            conditions.push(["==", ["get", "id"], hoveredFeatureId], highlightColor);
        }

        return conditions.length > 0
            ? (["case", ...conditions, baseColor] as DataDrivenPropertyValueSpecification<string>)
            : baseColor;
    };

    return [
        // Points
        {
            id: `${sourceId}-other-points`,
            source: sourceId,
            type: "circle",
            slot: "top",
            paint: {
                "circle-stroke-color": "#FFFFFF",
                "circle-stroke-opacity": isLayerSelected ? 1 : 0.4,
                "circle-stroke-width": isLayerSelected ? 2 : 0.5,
                "circle-color": makeConditionalFeatureExpression(color),
                "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10,
                    2,
                    18,
                    makeOnHoverExpression({
                        defaultValue: defaultRadius,
                        hoveredMapEditorFeatureId: hoveredFeatureId,
                        hoveredValue: defaultRadius + 4,
                    }),
                ],
                "circle-opacity": makeOnHoverExpression({
                    defaultValue: defaultOpacity,
                    hoveredMapEditorFeatureId: hoveredFeatureId,
                    hoveredValue: 1,
                }),
            },
            filter: ["==", "$type", "Point"],
        },

        // Lines
        {
            id: `${sourceId}-other-lines`,
            source: sourceId,
            type: "line",
            slot: "top",
            layout: {
                "line-cap": "round",
                "line-join": "round",
            },
            paint: {
                "line-width": isLayerSelected ? 6 : 3,
                "line-border-color": "#FFFFFF",
                "line-border-width": isLayerSelected ? 2 : 0,
                "line-color": makeConditionalFeatureExpression(color),
                "line-opacity": isLayerSelected ? 1 : 0.5,
            },
            filter: ["==", "$type", "LineString"],
        },

        // Polygons - Outline
        {
            id: `${sourceId}-other-polygons-outline`,
            source: sourceId,
            type: "line",
            slot: "top",
            paint: {
                "line-color": makeConditionalFeatureExpression(color),
                "line-width": makeOnHoverExpression({
                    defaultValue: 3.0,
                    hoveredMapEditorFeatureId: hoveredFeatureId,
                    hoveredValue: 5,
                }),
                "line-opacity": makeOnHoverExpression({
                    defaultValue: isLayerSelected ? 1 : 0.5,
                    hoveredMapEditorFeatureId: hoveredFeatureId,
                    hoveredValue: 1,
                }),
            },
            filter: ["==", "$type", "Polygon"],
        },

        // Polygons - Fill
        {
            id: `${sourceId}-other-polygons-fill`,
            source: sourceId,
            type: "fill",
            slot: "top",
            paint: {
                "fill-color": makeConditionalFeatureExpression(color),
                "fill-opacity": makeOnHoverExpression({
                    defaultValue: isLayerSelected ? 0.05 : 0.02,
                    hoveredMapEditorFeatureId: hoveredFeatureId,
                    hoveredValue: 0.25,
                }),
            },
            filter: ["==", "$type", "Polygon"],
        },
    ];
}
